import {
	getAlertTheme,
	readThemeCssVar,
} from "@/shared/notifications/alertTheme";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

type ToastTheme = ReturnType<typeof getToastTheme>;

function getToastTheme() {
	const alertTheme = getAlertTheme();
	const fallbackShadow =
		alertTheme.scheme === "dark"
			? "0 14px 32px rgba(0, 0, 0, 0.34)"
			: "0 10px 24px rgba(15, 23, 42, 0.12)";
	const fallbackTrack =
		alertTheme.scheme === "dark"
			? "rgba(159, 176, 199, 0.24)"
			: "rgba(82, 99, 122, 0.2)";

	return {
		...alertTheme,
		success: readThemeCssVar(
			"--satos-palette-success",
			alertTheme.confirmColor,
		),
		error: readThemeCssVar("--satos-palette-error", alertTheme.errorColor),
		warning: readThemeCssVar("--satos-palette-warning", alertTheme.denyColor),
		info: readThemeCssVar("--satos-palette-info", alertTheme.confirmColor),
		shadow: readThemeCssVar("--satos-toast-shadow", fallbackShadow),
		track: readThemeCssVar("--satos-toast-track", fallbackTrack),
		radius: readThemeCssVar(
			"--satos-surface-radius",
			alertTheme.scheme === "dark" ? "12px" : "10px",
		),
		borderWidth: readThemeCssVar("--satos-surface-border-width", "1px"),
	};
}

/** Inject toast styles once */
function ensureToastStyles(): void {
	if (typeof document === "undefined") return;
	const id = "satos-toast-styles";
	if (document.getElementById(id)) return;
	const style = document.createElement("style");
	style.id = id;
	style.textContent = `
		.satos-toast {
			border-radius: var(--satos-toast-radius, 10px) !important;
			box-shadow: var(--satos-toast-shadow) !important;
			border: var(--satos-toast-border-width, 1px) solid var(--satos-toast-border) !important;
			border-left: 4px solid var(--satos-toast-progress) !important;
			padding: 12px 14px !important;
			min-width: 280px;
			max-width: 420px;
			font-family: inherit;
			overflow: hidden !important;
		}
		.satos-toast .swal2-title {
			font-size: 15px !important;
			font-weight: 700 !important;
			line-height: 1.4 !important;
			padding: 0 !important;
			margin: 0 !important;
			color: var(--satos-toast-color) !important;
		}
		.satos-toast .swal2-html-container {
			font-size: 13px !important;
			line-height: 1.45 !important;
			color: var(--satos-toast-subtext) !important;
			margin: 2px 0 0 !important;
			padding: 0 !important;
		}
		.satos-toast .swal2-icon {
			margin: 0 12px 0 0 !important;
			width: 26px !important;
			height: 26px !important;
			border-width: 2px !important;
		}
		.satos-toast .swal2-icon-content {
			font-size: 1.15rem !important;
		}
		.satos-toast .swal2-timer-progress-bar-container {
			margin: 10px 0 0 !important;
			height: 4px !important;
			border-radius: 999px !important;
			overflow: hidden !important;
			background: var(--satos-toast-track) !important;
		}
		.satos-toast .swal2-timer-progress-bar {
			background: var(--satos-toast-progress) !important;
			border-radius: 999px !important;
		}
	`;
	document.head.appendChild(style);
}

function getProgressColor(
	theme: ToastTheme,
	icon: "success" | "error" | "info" | "warning",
): string {
	if (icon === "success") return theme.success;
	if (icon === "error") return theme.error;
	if (icon === "warning") return theme.warning;
	return theme.info;
}

function fireToast(opts: {
	icon: "success" | "error" | "info" | "warning";
	title: string | HTMLElement | JQuery | undefined;
	timer?: number;
	description?: string;
}) {
	ensureToastStyles();
	const theme = getToastTheme();
	const vars = {
		"--satos-toast-shadow": theme.shadow,
		"--satos-toast-border": theme.border,
		"--satos-toast-border-width": theme.borderWidth,
		"--satos-toast-color": theme.color,
		"--satos-toast-subtext": theme.subtext,
		"--satos-toast-track": theme.track,
		"--satos-toast-progress": getProgressColor(theme, opts.icon),
		"--satos-toast-radius": theme.radius,
	} as Record<string, string>;

	const Toast = MySwal.mixin({
		toast: true,
		position: "top-end",
		showConfirmButton: false,
		timer: opts.timer ?? 3000,
		timerProgressBar: true,
		background: theme.background,
		color: theme.color,
		customClass: {
			popup: `satos-toast satos-toast--${theme.scheme}`,
		},
		didOpen: (el) => {
			Object.assign(el.style, vars);
		},
	});

	const titleContent =
		opts.description &&
		typeof opts.title === "string" &&
		typeof opts.description === "string"
			? `<span style="display:block;font-weight:600;color:inherit;">${escapeHtml(opts.title)}</span><span style="display:block;font-size:13px;opacity:0.9;margin-top:2px;">${escapeHtml(opts.description)}</span>`
			: opts.title;

	return Toast.fire({
		icon: opts.icon,
		title: titleContent,
	});
}

function escapeHtml(s: string): string {
	const div = document.createElement("div");
	div.textContent = s;
	return div.innerHTML;
}

// --- Default copy (clear, friendly) ---
const DEFAULT_SUCCESS_TITLE = "Saved successfully";
const DEFAULT_SUCCESS_DESCRIPTION = "Your changes have been applied.";
const DEFAULT_ERROR_TITLE = "Something went wrong";
const DEFAULT_ERROR_DESCRIPTION =
	"Please try again or contact support if it persists.";
const DEFAULT_INFO_DESCRIPTION = "Here's some useful information.";
const DEFAULT_WARNING_TITLE = "Warning";
const DEFAULT_WARNING_DESCRIPTION = "Please review this before continuing.";

export class AlertToast {
	/** Show a success toast. Pass a message to override the default. */
	public static success = async (message?: string | HTMLElement | JQuery) => {
		const title = message ?? DEFAULT_SUCCESS_TITLE;
		const description =
			message === undefined ? DEFAULT_SUCCESS_DESCRIPTION : undefined;
		await fireToast({
			icon: "success",
			title,
			description,
			timer: 2500,
		});
	};

	/** Show an error toast. Pass a message to override the default. */
	public static failed = async (message?: string | HTMLElement | JQuery) => {
		const title = message ?? DEFAULT_ERROR_TITLE;
		const description =
			message === undefined ? DEFAULT_ERROR_DESCRIPTION : undefined;
		await fireToast({
			icon: "error",
			title,
			description,
			timer: 4000,
		});
	};
}

/** Success toast with custom message (and optional description). */
export const SuccessToast = async (
	message: string | HTMLElement | JQuery | undefined,
	description?: string,
) => {
	await fireToast({
		icon: "success",
		title: message ?? DEFAULT_SUCCESS_TITLE,
		description:
			description ?? (message ? undefined : DEFAULT_SUCCESS_DESCRIPTION),
		timer: 3000,
	});
};

/** Info toast with custom message. */
export const InfoToast = (
	message: string | HTMLElement | JQuery | undefined,
	description?: string,
) => {
	void fireToast({
		icon: "info",
		title: message ?? "Information",
		description:
			description ?? (message ? undefined : DEFAULT_INFO_DESCRIPTION),
		timer: 3000,
	});
};

/** Error toast with custom message. */
export const ErrorToast = (
	message: string | HTMLElement | JQuery | undefined,
	description?: string,
) => {
	void fireToast({
		icon: "error",
		title: message ?? DEFAULT_ERROR_TITLE,
		description:
			description ?? (message ? undefined : DEFAULT_ERROR_DESCRIPTION),
		timer: 4000,
	});
};

/** Warning toast with custom message. */
export const WarningToast = (
	message: string | HTMLElement | JQuery | undefined,
	description?: string,
) => {
	void fireToast({
		icon: "warning",
		title: message ?? DEFAULT_WARNING_TITLE,
		description:
			description ?? (message ? undefined : DEFAULT_WARNING_DESCRIPTION),
		timer: 3500,
	});
};
